import NoCurrentFloorFacilityPopup from 'components/popup/toast/no-facility/NoCurrentFloorFacilityPopup';
import useUsageMetric from 'hooks/api/metric/useUsageMetric';
import { clearAllMarkers } from 'map/control/common/clear';
import { drawFacilityMarkers } from 'map/control/facilityMarker';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useCategoryGroupStore from 'store/categoryGroup';
import useColorSetStore from 'store/colorSet';
import useFacilityStore from 'store/facility';
import useFloorStore from 'store/floor';
import useLanguageStore from 'store/languages';
import useMachineStore from 'store/machine';
import usePopupStore from 'store/popup';
import { CategoryGroup } from 'types/category/category.type';
import { Marker } from 'types/map/marker.type';
import { TenantWithPointId } from 'types/tenant/tenant.type';
import styles from './FacilityBar.module.scss';
import FacilityItem from './facility-item/FacilityItem';

type FacilityBarProps = {
  isOpenNoFacilityToast: boolean;
  removeNoFacilityToast: () => void;
  openNoFacilityToast: () => void;
};

const FacilityBar = ({ isOpenNoFacilityToast, removeNoFacilityToast, openNoFacilityToast }: FacilityBarProps) => {
  const { t } = useTranslation();
  const categoryStore = useCategoryGroupStore();
  const floorStore = useFloorStore();
  const langStore = useLanguageStore();
  const facilityStore = useFacilityStore();
  const popupStore = usePopupStore();
  const colorSetStore = useColorSetStore();
  const machineStore = useMachineStore();

  const [popupMessage, setPopupMessage] = useState('');

  const { postCategoryUsage } = useUsageMetric();

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const closePopupAfterSeconds = () => {
    timerRef.current = setTimeout(() => {
      removeNoFacilityToast();
    }, 3000);
  };

  const clearPopupTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    removeNoFacilityToast();
  };

  const openToastPopup = () => {
    openNoFacilityToast();
    closePopupAfterSeconds();
  };

  // 해당 카테고리에 포함된 테넌트 검색
  const findTenants = (categoryGroupId: string): TenantWithPointId[] | undefined => {
    if (categoryStore.mapFacilityCategories) {
      const origin = [...categoryStore.mapFacilityCategories.tenants];

      const tenants = origin.filter(tenant => {
        return tenant.categoryGroups.some(category => category.id === categoryGroupId);
      });

      return tenants;
    }
  };

  const setupSearchedFacilityMessage = (facility: string, nearestFloorMarker: Marker | undefined) => {
    if (typeof nearestFloorMarker === 'undefined') {
      setPopupMessage(t(`floor.noFacility`, { facility }));
    }

    if (typeof nearestFloorMarker !== 'undefined') {
      const findFloor = floorStore.floors.find(floor => floor.id === nearestFloorMarker.floorId);
      if (!findFloor) return;
      setPopupMessage(t(`floor.nearestFacility`, { facility, floor: findFloor.name[langStore.currentLang] }));
    }
  };

  const handleClickItem = async (categoryGroup: CategoryGroup) => {
    if (!machineStore.machine) {
      return;
    }

    // 카테고리 통계 수집
    await postCategoryUsage(categoryGroup.id, machineStore.machine.point.id, machineStore.machine.id);

    clearPopupTimer();

    popupStore.setOpenToastPopup(false);
    facilityStore.setCurrentFacilityId(categoryGroup.id);

    // TODO: 할때마다 찾지말고 map 으로 저장해놓고 찾도록 변경
    const tenants = findTenants(categoryGroup.id);
    if (tenants) {
      const { currentFloorMarkers, nearestFloorMarker } = await drawFacilityMarkers(tenants, floorStore.currentFloorId);

      // 현재 층의 마커가 없다면 토스트팝업 노출
      if (typeof currentFloorMarkers === 'undefined' || currentFloorMarkers.length === 0) {
        setupSearchedFacilityMessage(categoryGroup.name[langStore.currentLang], nearestFloorMarker);
        openToastPopup();
        return;
      }

      removeNoFacilityToast();
    }
  };

  useEffect(() => {
    return () => {
      clearPopupTimer();
      clearAllMarkers();
      facilityStore.resetCurrentFacilityId();
    };
  }, []);

  return (
    <div
      className={styles.facility_bar}
      style={{
        background: colorSetStore.header.bg,
        borderTop: `1px solid ${colorSetStore.sub}`,
      }}
    >
      <div className={styles.facilities}>
        {categoryStore.mapFacilityCategories?.categoryGroups
          // .filter(group => group.tenants?.length > 0)
          .map(categoryGroup => (
            <FacilityItem
              key={categoryGroup.id ?? ''}
              categoryGroup={categoryGroup}
              handleClickItem={handleClickItem}
              isActive={categoryGroup.id === facilityStore.currentFacilityId}
            />
          ))}
      </div>

      {/* css animation 으로 display 처리 */}
      <NoCurrentFloorFacilityPopup
        isOpen={isOpenNoFacilityToast}
        message={popupMessage}
        clearPopupTimer={clearPopupTimer}
      />
    </div>
  );
};
export default FacilityBar;
